import React from 'react';
import { Server, Cable, Network, Sun, Cpu, Users, CheckCircle } from 'lucide-react';
import { services } from '../data/mock';
import { Button } from '../components/ui/button';

const iconComponents = {
  Server,
  Cable,
  Network,
  Sun,
  Cpu,
  Users
};

const Services = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1607723619497-98a79f01ba90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHxmaWJlciUyMG9wdGljJTIwY2FibGV8ZW58MHx8fHwxNzcwMjAxNDIwfDA&ixlib=rb-4.1.0&q=85"
            alt="Fiber Optic"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-900/90 to-cyan-950/80"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Technical <span className="text-cyan-400">Services</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Comprehensive ISP and network infrastructure solutions from deployment to maintenance
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = iconComponents[service.icon];
              return (
                <div 
                  key={service.id}
                  className="bg-slate-900 border border-slate-700 rounded-2xl p-8 hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10 group"
                >
                  <div className="w-16 h-16 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-cyan-500/20 transition-colors duration-300">
                    <IconComponent className="text-cyan-400" size={32} />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {service.title}
                  </h3>
                  
                  <p className="text-gray-400 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <ul className="space-y-3">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300">
                        <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Our Implementation Process
            </h2>
            <p className="text-xl text-gray-400">
              From initial consultation to full deployment and support
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-cyan-500">
                <span className="text-3xl font-bold text-cyan-400">1</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Consultation</h3>
              <p className="text-gray-400 text-sm">
                Assess your needs, coverage area, and technical requirements
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-cyan-500">
                <span className="text-3xl font-bold text-cyan-400">2</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Design</h3>
              <p className="text-gray-400 text-sm">
                Custom network architecture tailored to your deployment scale
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-cyan-500">
                <span className="text-3xl font-bold text-cyan-400">3</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Deployment</h3>
              <p className="text-gray-400 text-sm">
                Professional installation with carrier-grade equipment
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-cyan-500">
                <span className="text-3xl font-bold text-cyan-400">4</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Support</h3>
              <p className="text-gray-400 text-sm">
                24/7 monitoring, maintenance, and technical assistance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Showcase */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <img 
                src="https://images.pexels.com/photos/4280696/pexels-photo-4280696.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Fiber Infrastructure"
                className="rounded-2xl w-full h-full object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-white">
                Enterprise-Grade FTTH Deployment
              </h2>
              <p className="text-gray-400 leading-relaxed">
                Our Fiber-to-the-Home solutions utilize industry-leading practices and equipment to deliver reliable, high-speed connectivity.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-300">
                  <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm">Single-mode fiber for long-distance runs</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm">Professional fusion splicing and testing</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm">GPON/EPON technology for scalability</span>
                </li>
                <li className="flex items-start text-gray-300">
                  <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={18} />
                  <span className="text-sm">Weatherproof infrastructure for durability</span>
                </li>
              </ul>
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-xl transition-all duration-200">
                Request Quote
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-cyan-950/30 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Need Custom Infrastructure?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact our technical team for consultation on your specific deployment requirements.
          </p>
          <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-200">
            Schedule Technical Consultation
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
